import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api.js';
import { formatDateWithTimeYear } from '../../utils/dateFormat.js';
import { usePersistedFilterState } from './utils/usePersistedFilterState.js';

const LIMIT = 10;

const STATUS_OPTIONS = [
    { value: null,  label: 'All' },
    { value: 0,     label: 'Pending' },
    { value: 1,     label: 'Approved' },
    { value: 2,     label: 'Rejected' },
];

const STATUS_BADGE = {
    0: 'bg-yellow-100 text-yellow-700',
    1: 'bg-green-100 text-green-700',
    2: 'bg-red-100 text-red-700',
};

const statusLabel = (s) => STATUS_OPTIONS.find(o => o.value === s)?.label ?? String(s);

const InvoiceRequests = ({ isActive }) => {
    const [data, setData]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset]   = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal]     = useState(0);
    const [status, setStatus]   = usePersistedFilterState('alpha_designer.invoice_requests.status', null);

    const fetchInvoiceRequests = useCallback(async (statusFilter, currentOffset, append = false) => {
        setLoading(true);
        try {
            const res = await api({
                url: '/alpha_cst_designer/invoiceRequests',
                method: 'POST',
                responseType: 'json',
                data: { status: statusFilter, offset: currentOffset, limit: LIMIT },
            });
            if (res.code === 200) {
                const newData    = res.data?.data  || [];
                const totalCount = res.data?.total || 0;
                setData(prev => append ? [...prev, ...newData] : newData);
                setTotal(totalCount);
                setHasMore(currentOffset + LIMIT < totalCount);
            }
        } catch (err) {
            console.error('Error fetching invoice requests:', err);
            if (!append) setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isActive) return;
        setOffset(0);
        setData([]);
        fetchInvoiceRequests(status, 0, false);
    }, [isActive, status, fetchInvoiceRequests]);

    const handleLoadMore = () => {
        if (loading || !hasMore) return;
        const nextOffset = offset + LIMIT;
        setOffset(nextOffset);
        fetchInvoiceRequests(status, nextOffset, true);
    };

    return (
        <div className="p-4">
            {/* Header row */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex gap-1">
                    {STATUS_OPTIONS.map(opt => (
                        <button
                            key={String(opt.value)}
                            onClick={() => { setStatus(opt.value); setOffset(0); }}
                            className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                                status === opt.value
                                    ? 'bg-sky-600 text-white border-sky-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-sky-400'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto px-3 py-1 shadow rounded-lg bg-gray-200 text-sm">
                    Total: <span className="font-semibold">{total}</span>
                </div>
            </div>

            {/* Table */}
            {loading && data.length === 0 ? (
                <div className="flex justify-center py-16 text-gray-400">Loading...</div>
            ) : data.length === 0 ? (
                <div className="flex justify-center py-16 text-gray-400">No invoice requests found.</div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="px-4 py-3 text-left whitespace-nowrap">S.No</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Order ID</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Requested By</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Status</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Approved By</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Approval Time</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Rejected By</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Rejection Time</th>
                                <th className="px-4 py-3 text-left whitespace-nowrap">Created At</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.map((row, idx) => (
                                <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 text-gray-500">{offset + idx + 1}</td>
                                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{row.order_id || '-'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="font-medium text-gray-800">{row.requested_by_name || '-'}</div>
                                        <div className="text-xs text-gray-400">ID: {row.requested_by}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[row.status] ?? 'bg-gray-100 text-gray-600'}`}>
                                            {statusLabel(row.status)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.approved_by_name || '-'}</td>
                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                        {row.approval_time ? formatDateWithTimeYear(row.approval_time) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.rejected_by_name || '-'}</td>
                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                        {row.rejected_time ? formatDateWithTimeYear(row.rejected_time) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                        {row.created_at ? formatDateWithTimeYear(row.created_at) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Load more */}
            {hasMore && (
                <div className="flex justify-center mt-4">
                    <button
                        className="px-5 py-2 bg-sky-600 text-white rounded-md text-sm disabled:opacity-50 hover:bg-sky-700 transition-colors"
                        onClick={handleLoadMore}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Load More'}
                    </button>
                </div>
            )}
            {loading && data.length > 0 && (
                <div className="flex justify-center mt-3 text-gray-400 text-sm">Loading more...</div>
            )}
        </div>
    );
};

export default InvoiceRequests;
